(() => {
  const STORAGE_KEY = 'recently-viewed-products';
  const HARD_LIMIT = 50;

  function readHandles() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string' && h) : [];
    } catch (error) {
      return [];
    }
  }

  function writeHandles(handles) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (error) {
      return;
    }
  }

  function trackCurrentProduct(handle) {
    if (!handle) return readHandles();
    const existing = readHandles().filter((h) => h !== handle);
    const updated = [handle, ...existing].slice(0, HARD_LIMIT);
    writeHandles(updated);
    return updated;
  }

  class RecentlyViewedProducts extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.currentHandle = this.dataset.productHandle || '';
      this.maxProducts = parseInt(this.dataset.maxProducts, 10) || 8;
      this.minProducts = parseInt(this.dataset.minProducts, 10) || 1;
      this.slidesMobile = Number(this.dataset.slidesMobile) || 1;
      this.slidesTablet = Number(this.dataset.slidesTablet) || 2;
      this.slidesDesktop = Number(this.dataset.slidesDesktop) || 4;
      this.showSecondaryImage = this.dataset.showSecondaryImage === 'true';

      this.swiperEl = this.querySelector('[data-recently-viewed-swiper]');
      this.wrapper = this.querySelector('[data-recently-viewed-wrapper]');
      this.loadingEl = this.querySelector('[data-recently-viewed-loading]');
      this.progressEl = this.querySelector('[data-recently-viewed-progress]');

      this.init();
    }

    async init() {
      trackCurrentProduct(this.currentHandle);

      const handles = readHandles()
        .filter((handle) => handle !== this.currentHandle)
        .slice(0, this.maxProducts);

      if (handles.length < this.minProducts) {
        this.finish(false);
        return;
      }

      const cards = await Promise.all(handles.map((handle) => this.fetchCard(handle)));
      const validCards = cards.filter(Boolean);

      if (validCards.length < this.minProducts) {
        this.finish(false);
        return;
      }

      this.render(validCards);
      this.finish(true);
      this.initSlider();
    }

    async fetchCard(handle) {
      const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const url = `${rootUrl}products/${encodeURIComponent(handle)}?section_id=recently-viewed-card`;

      try {
        const response = await fetch(url);
        if (!response.ok) return null;

        const html = (await response.text()).trim();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const slide = parsed.querySelector('.swiper-slide');
        if (!slide) return null;

        if (!this.showSecondaryImage) {
          const hoverMedia = slide.querySelector('.media--hover-effect');
          const images = hoverMedia ? hoverMedia.querySelectorAll('img') : [];
          if (images.length > 1) images[images.length - 1].remove();
        }

        return slide.outerHTML;
      } catch (error) {
        return null;
      }
    }

    render(cardHtmlList) {
      const template = document.createElement('template');
      template.innerHTML = cardHtmlList.join('');
      this.wrapper.replaceChildren(template.content);
    }

    initSlider() {
      if (!this.swiperEl || typeof Swiper === 'undefined') {
        this.initCardMedia();
        return;
      }

      const swiper = new Swiper(this.swiperEl, this.getSwiperOptions());
      this.swiper = swiper;

      this.swiperEl.classList.add('related-products__swiper--initialized');
      this.bindProgress(swiper);
      this.syncNavVisibility(swiper);

      swiper.on('lock', () => this.syncNavVisibility(swiper));
      swiper.on('unlock', () => this.syncNavVisibility(swiper));
      swiper.on('resize', () => this.syncNavVisibility(swiper));

      this.initCardMedia();
    }

    getSwiperOptions() {
      const options = {
        speed: 500,
        loop: false,
        slidesPerView: this.slidesMobile,
        spaceBetween: 16,
        breakpoints: {
          750: {
            slidesPerView: this.slidesTablet,
            spaceBetween: 20,
          },
          990: {
            slidesPerView: this.slidesDesktop,
            spaceBetween: 19,
          },
        },
        grabCursor: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
      };

      const prevEl = this.querySelector('[data-recently-viewed-prev]');
      const nextEl = this.querySelector('[data-recently-viewed-next]');

      if (prevEl && nextEl) {
        options.navigation = {
          prevEl,
          nextEl,
          disabledClass: 'swiper-button-disabled',
        };
      }

      return options;
    }

    syncNavVisibility(swiper) {
      const nav = this.querySelector('[data-recently-viewed-nav]');
      if (!nav || !swiper) return;

      const lock = Boolean(swiper.isLocked);
      nav.classList.toggle('is-hidden', lock);
      nav.setAttribute('aria-hidden', lock ? 'true' : 'false');
    }

    bindProgress(swiper) {
      const fill = this.progressEl?.querySelector('.related-products__progress-fill');
      if (!this.progressEl || !fill) return;

      const update = () => {
        if (!swiper.slides.length) return;

        this.progressEl.hidden = Boolean(swiper.isLocked);

        const total = swiper.slides.length;
        const ratio = typeof swiper.progress === 'number' ? swiper.progress : 0;
        const minPct = Math.min((3 / Math.max(total, 3)) * 100, 40);

        if (ratio <= 0.001) {
          fill.style.width = `${Math.max(minPct, 8)}%`;
        } else if (swiper.isEnd || ratio >= 0.999) {
          fill.style.width = '100%';
        } else {
          fill.style.width = `${Math.min(Math.max(ratio * 100, minPct), 100)}%`;
        }
      };

      swiper.on('slideChange', update);
      swiper.on('progress', update);
      swiper.on('resize', update);
      update();
    }

    initCardMedia() {
      if (typeof window.initCardMediaSliders === 'function') {
        window.initCardMediaSliders(this);
      }
    }

    finish(hasProducts) {
      if (this.loadingEl) this.loadingEl.remove();

      if (hasProducts) {
        this.hidden = false;
      } else {
        this.remove();
      }
    }
  }

  if (!customElements.get('recently-viewed-products')) {
    customElements.define('recently-viewed-products', RecentlyViewedProducts);
  }
})();
